import { FC, useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Alert, Col, Row } from 'react-bootstrap'
import { api } from '../../../../../auth'
import { useAppSelector } from '../../../../../store/hooks'
import PageHeader from '../../../../layout/page/PageHeader'
import Common from '../../../../common/Common'
import { BuilderPageListItem } from '../types'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
    faPlus,
    faFileLines,
    faArrowRight,
} from '@fortawesome/free-solid-svg-icons'
import DeleteDialog from '../../../../common/dialogs/DeleteDialog'
import PageForm from '../../pages/PageForm'

const GRADIENTS = [
    'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
    'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
    'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
    'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
    'linear-gradient(135deg, #a18cd1 0%, #fbc2eb 100%)',
    'linear-gradient(135deg, #fccb90 0%, #d57eeb 100%)',
    'linear-gradient(135deg, #e0c3fc 0%, #8ec5fc 100%)',
]

const getInitials = (name: string): string => {
    return name
        .split(' ')
        .map((w) => w[0])
        .join('')
        .toUpperCase()
        .slice(0, 2)
}

const PageList: FC = () => {
    const navigate = useNavigate()
    const theme = useAppSelector((state) => state.theme.theme)
    const axiosPrivate = api

    const [pages, setPages] = useState<BuilderPageListItem[]>([])
    const [isLoading, setIsLoading] = useState(true)
    const [errMsg, setErrMsg] = useState('')
    const [refreshList, setRefreshList] = useState(false)
    const [showCreatePage, setShowCreatePage] = useState(false)

    const loadPages = useCallback(async () => {
        setErrMsg('')
        setIsLoading(true)
        try {
            const response = await axiosPrivate
                .get('/page-components/builder/page/new')
                .finally(() => setIsLoading(false))

            if (response.data?.success === true) {
                setPages(response.data.data.available_pages || [])
            } else {
                setErrMsg(
                    response.data?.errors?.join(', ') || 'Failed to load pages'
                )
            }
        } catch {
            setErrMsg('Failed to load pages')
        }
    }, [axiosPrivate])

    useEffect(() => {
        loadPages()
    }, [loadPages])

    useEffect(() => {
        if (refreshList) {
            loadPages()
            setRefreshList(false)
        }
    }, [refreshList, loadPages])

    return (
        <div className="sfy-page-builder-list">
            <PageHeader
                icon="wand-magic-sparkles"
                subtitle="Design and arrange your pages visually"
                header="Page Builder"
                gradient="linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                buttons={[
                    {
                        variant: 'success',
                        label: 'Create New Page',
                        icon: 'fa-solid fa-plus',
                        onClick: () => setShowCreatePage(true),
                    },
                ]}
            />

            {isLoading && <Common.Loader text="Loading pages..." />}

            {errMsg && (
                <Alert variant="danger" className="mt-3">
                    {errMsg}
                </Alert>
            )}

            {!isLoading && pages.length === 0 && !errMsg && (
                <div className="sfy-builder-empty-state">
                    <div className="sfy-builder-empty-state__icon">
                        <FontAwesomeIcon icon={faFileLines} />
                    </div>
                    <h3>No pages yet</h3>
                    <p>Create your first page to start building.</p>
                    <button
                        className="btn btn-primary btn-lg"
                        onClick={() => setShowCreatePage(true)}
                    >
                        <FontAwesomeIcon icon={faPlus} className="me-2" />
                        Create Your First Page
                    </button>
                </div>
            )}

            {!isLoading && pages.length > 0 && (
                <Row className="g-4 mt-1">
                    {pages.map((page, index) => (
                        <Col key={page.id} sm={6} lg={4} xl={3}>
                            <div
                                className={`sfy-page-card ${
                                    theme === 'dark' ? 'sfy-page-card--dark' : ''
                                }`}
                                style={{ animationDelay: `${index * 60}ms` }}
                            >
                                <div
                                    className="sfy-page-card__header"
                                    style={{
                                        background:
                                            GRADIENTS[index % GRADIENTS.length],
                                    }}
                                >
                                    <span className="sfy-page-card__initials">
                                        {getInitials(page.name)}
                                    </span>
                                </div>
                                <div className="sfy-page-card__body">
                                    <h5 className="sfy-page-card__title">
                                        {page.name}
                                    </h5>
                                    {page.code && (
                                        <span className="sfy-page-card__url">
                                            /{page.code}
                                        </span>
                                    )}
                                </div>
                                <div className="sfy-page-card__actions">
                                    <DeleteDialog
                                        url={
                                            '/page-components/pages/delete/' +
                                            page.id
                                        }
                                        record={page}
                                        deletedCallback={() =>
                                            setRefreshList(true)
                                        }
                                        message={
                                            <p>
                                                Are you sure you want to delete
                                                page{' '}
                                                <strong>{page.name}</strong>?
                                                All sections will be removed.
                                            </p>
                                        }
                                    />
                                    <button
                                        className="btn btn-sm btn-primary sfy-page-card__edit-btn"
                                        onClick={() =>
                                            navigate(
                                                `/content/page-builder/${page.id}`
                                            )
                                        }
                                    >
                                        Open Builder
                                        <FontAwesomeIcon
                                            icon={faArrowRight}
                                            className="ms-2"
                                        />
                                    </button>
                                </div>
                            </div>
                        </Col>
                    ))}
                </Row>
            )}

            {showCreatePage && (
                <PageForm
                    inPopup={true}
                    onClose={() => {
                        setShowCreatePage(false)
                        setRefreshList(true)
                    }}
                />
            )}
        </div>
    )
}

export default PageList
